import { useEffect, useRef, useState } from "react";
import { IoSaveOutline } from "react-icons/io5";
import { uploadMedia } from "../../../api/mediaApi";
import { getApiErrorMessage } from "../../../api/unwrapApiResponse";
import { useNotify } from "../../../components/ui/NotificationProvider";
import FieldInputRenderer from "../fields/FieldInputRenderer";
import {
  EMPTY_PROJECT_FORM,
  PROJECT_DETAIL_FIELD_DEFS,
  PROJECT_FIELD_DEFS,
  PROJECT_LIST_FIELD_DEFS,
  PROJECT_RESOURCE_FIELD_DEFS,
  PROJECT_SERVICE_FIELD_DEFS,
  PROJECT_TECH_FIELD_DEFS,
  slugifyTitle,
} from "./projectFieldUtils";

const TABS = [
  { id: "card", label: "Card" },
  { id: "detail", label: "Detail page" },
];

export default function ProjectFormModal({
  open,
  sectionId,
  initialForm,
  editingProjectKey = null,
  isEdit = false,
  busy,
  featuredMax = 5,
  featuredCount = 0,
  onClose,
  onSubmit,
  onSaveField,
}) {
  const notify = useNotify();
  const [form, setForm] = useState(EMPTY_PROJECT_FORM);
  const [tab, setTab] = useState("card");
  const [uploadBusyKey, setUploadBusyKey] = useState(null);
  const initialSnapshotRef = useRef(EMPTY_PROJECT_FORM);

  useEffect(() => {
    if (!open) return;
    const snapshot = initialForm
      ? { ...EMPTY_PROJECT_FORM, ...initialForm }
      : { ...EMPTY_PROJECT_FORM };
    initialSnapshotRef.current = snapshot;
    setForm(snapshot);
    setTab("card");
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, editingProjectKey]);

  if (!open) return null;

  const hasChanges = PROJECT_FIELD_DEFS.some((def) => {
    const current = form[def.suffix];
    const initial = initialSnapshotRef.current[def.suffix];
    if (def.fieldType === "boolean") {
      return Boolean(current) !== Boolean(initial);
    }
    return (current ?? "") !== (initial ?? "");
  });

  function setField(suffix, value) {
    setForm((prev) => ({ ...prev, [suffix]: value }));
  }

  function handleTitleBlur() {
    if (isEdit) return;
    if (!form.slug.trim() && form.title.trim()) {
      setForm((prev) => ({ ...prev, slug: slugifyTitle(prev.title) }));
    }
  }

  function handleFeaturedChange(next) {
    const wasFeatured = Boolean(initialSnapshotRef.current.featured);
    if (next && !wasFeatured && featuredCount >= featuredMax) {
      notify.error(`You can feature at most ${featuredMax} projects.`);
      return;
    }
    setField("featured", next);
  }

  async function handleUpload(suffix, file) {
    setUploadBusyKey(suffix);
    try {
      const formData = new FormData();
      formData.append("file", file);
      formData.append("mediaType", "image");
      formData.append("groupKey", "work-projects");
      formData.append("altText", file.name);
      formData.append("isPrimary", "false");
      formData.append("sortOrder", "0");

      const uploaded = await uploadMedia(sectionId, formData);
      const url = uploaded?.url ?? uploaded?.Url ?? "";
      if (!url) throw new Error("Upload did not return a URL");

      setField(suffix, url);

      if (isEdit && onSaveField) {
        await onSaveField(suffix, url);
        initialSnapshotRef.current = {
          ...initialSnapshotRef.current,
          [suffix]: url,
        };
      }

      notify.success(isEdit ? "Image saved" : "Image uploaded");
    } catch (err) {
      notify.error(getApiErrorMessage(err));
    } finally {
      setUploadBusyKey(null);
    }
  }

  function handleSubmit(e) {
    e.preventDefault();
    onSubmit({
      ...form,
      slug: form.slug.trim().toLowerCase(),
    });
  }

  function renderField(def) {
    const isMedia = def.fieldType === "image";
    const value =
      def.fieldType === "boolean" ? Boolean(form[def.suffix]) : form[def.suffix] ?? "";

    return (
      <FieldInputRenderer
        key={def.suffix}
        field={{
          key: def.suffix,
          label: def.label,
          fieldType: def.fieldType,
          isRequired: def.suffix === "title" || def.suffix === "slug",
        }}
        value={value}
        onChange={(next) => {
          if (def.suffix === "featured") {
            handleFeaturedChange(next);
            return;
          }
          setField(def.suffix, next);
        }}
        onUpload={isMedia ? (file) => handleUpload(def.suffix, file) : undefined}
        uploadBusy={uploadBusyKey === def.suffix}
      />
    );
  }

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
      <button
        type="button"
        aria-label="Close"
        className="absolute inset-0 bg-slate-900/40"
        onClick={onClose}
      />
      <form
        onSubmit={handleSubmit}
        className="relative flex max-h-[90vh] w-full max-w-4xl flex-col overflow-hidden rounded-2xl border border-gray-200 bg-white shadow-xl"
      >
        <div className="border-b border-gray-100 px-6 py-5">
          <h2 className="text-lg font-semibold text-slate-900">
            {isEdit ? "Edit project" : "New project"}
          </h2>
          <p className="mt-1 text-sm text-slate-500">
            Card fields power the Work grid. Detail fields power the project page.
          </p>
          <div className="mt-4 flex gap-2">
            {TABS.map((item) => (
              <button
                key={item.id}
                type="button"
                onClick={() => setTab(item.id)}
                className={`rounded-lg px-3 py-1.5 text-sm font-medium ${
                  tab === item.id
                    ? "bg-slate-900 text-white"
                    : "bg-slate-100 text-slate-600 hover:bg-slate-200"
                }`}
              >
                {item.label}
              </button>
            ))}
          </div>
        </div>

        <div className="flex-1 overflow-y-auto px-6 py-5">
          {tab === "card" ? (
            <div className="grid gap-6 md:grid-cols-2">
              <div className="space-y-4">
                <p className="text-xs font-semibold uppercase tracking-wide text-slate-400">
                  Listing
                </p>
                {PROJECT_LIST_FIELD_DEFS.map((def) => (
                  <div
                    key={def.suffix}
                    onBlur={def.suffix === "title" ? handleTitleBlur : undefined}
                  >
                    {renderField(def)}
                  </div>
                ))}
              </div>
              <div className="space-y-4">
                <p className="text-xs font-semibold uppercase tracking-wide text-slate-400">
                  Sidebar services
                </p>
                {PROJECT_SERVICE_FIELD_DEFS.map((def) => renderField(def))}
              </div>
            </div>
          ) : (
            <div className="space-y-6">
              <div className="grid gap-4 md:grid-cols-2">
                {PROJECT_DETAIL_FIELD_DEFS.slice(0, 8).map((def) =>
                  renderField(def)
                )}
              </div>
              <div>
                <p className="mb-3 text-xs font-semibold uppercase tracking-wide text-slate-400">
                  Other resources
                </p>
                <div className="grid gap-4 md:grid-cols-3">
                  {PROJECT_RESOURCE_FIELD_DEFS.map((def) => renderField(def))}
                </div>
              </div>
              <div className="grid gap-4 md:grid-cols-2">
                {PROJECT_DETAIL_FIELD_DEFS.slice(8).map((def) => renderField(def))}
              </div>
              <div>
                <p className="mb-3 text-xs font-semibold uppercase tracking-wide text-slate-400">
                  Tech stack
                </p>
                <div className="grid gap-4 md:grid-cols-2">
                  {PROJECT_TECH_FIELD_DEFS.map((def) => renderField(def))}
                </div>
              </div>
            </div>
          )}
        </div>

        <div className="flex justify-end gap-3 border-t border-gray-100 px-6 py-4">
          <button
            type="button"
            onClick={onClose}
            disabled={busy}
            className="rounded-xl border border-gray-200 px-4 py-2 text-sm font-medium text-slate-700"
          >
            Cancel
          </button>
          <button
            type="submit"
            disabled={
              busy ||
              (isEdit
                ? !hasChanges
                : !form.title.trim() || !form.slug.trim())
            }
            className="inline-flex items-center gap-2 rounded-xl bg-slate-900 px-4 py-2 text-sm font-medium text-white disabled:opacity-60"
          >
            <IoSaveOutline className="h-4 w-4" />
            {busy ? "Saving…" : isEdit ? "Save project" : "Create project"}
          </button>
        </div>
      </form>
    </div>
  );
}
